import React from 'react';
import { Link } from 'src/components/Link';
import { useQuery } from '@tanstack/react-query';
import { apiGetRequest } from 'src/utils/mlPigeonAPIRequest';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';
import { ClientSideTable } from 'src/components/ClientSideTable';
import { useNavigate } from 'react-router-dom';

interface Props {
  awsAccount: string | undefined;
}

export const SharedModelsTable = ({ awsAccount }: Props) => {
  const navigate = useNavigate();
  const { isLoading, error, data } = useQuery<ModelRegistryModel[], Error>({
    queryKey: ['registry-models', awsAccount],
    queryFn: () =>
      apiGetRequest({
        endpoint: 'sharedModels',
        params: {
          awsAccount: awsAccount || '',
        },
      })
        .then((res) => {
          return res.sharedModels.filter(
            (model: ModelRegistryModel) => !model.deprecated,
          );
        })
        .catch((err) => {
          if (
            err.message ===
            'No objects of type SharedModel found for given criteria'
          ) {
            return [];
          }

          throw new Error(err);
        }),
  });

  return (
    <ClientSideTable
      variant="container"
      resizableColumns
      actions={
        <PermissionAwareButton
          onClick={() => {
            navigate('./reserveRegistryName');
          }}
          variant="primary"
          requiredPermission="ADMINISTER"
          actionDescription="reserve shared model name"
        >
          Reserve a Name
        </PermissionAwareButton>
      }
      columnDefinitions={[
        {
          id: 'modelName',
          header: 'Shared Model Name',
          cell: (item: ModelRegistryModel) => (
            <Link to={`/modelRegistry/model/${item?.modelId}`}>
              {item.modelName}
            </Link>
          ),
          sortingField: 'modelName',
          isRowHeader: true,
        },

        {
          id: 'modelId',
          header: 'Model Id',
          cell: (item: ModelRegistryModel) => item.modelId,
          sortingField: 'modelId',
        },
        {
          id: 'deprecated',
          header: 'Deprecated',
          cell: (item: ModelRegistryModel) => item.deprecated?.toString(),
          sortingField: 'deprecated',
        },
        {
          id: 'createdDate',
          header: 'Created Date',
          cell: (item: ModelRegistryModel) => {
            const date = new Date(item.createdDate * 1000);
            return date.toUTCString();
          },
          sortingField: 'createdDate',
        },
        {
          id: 'updatedDate',
          header: 'Updated Date',
          cell: (item: ModelRegistryModel) => {
            const date = new Date(item.updatedDate * 1000);
            return date.toUTCString();
          },
          sortingField: 'updatedDate',
        },
        {
          id: 'updatedBy',
          header: 'Updated By',
          cell: (item: ModelRegistryModel) => item.updatedBy,
          sortingField: 'updatedBy',
        },
      ]}
      data={data ?? []}
      emptyText="No Shared Models found"
      title="Shared Models"
      error={error}
      loading={isLoading}
      sortColumn="name"
      isDescending={false}
    />
  );
};
